import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../firebase/AuthContext";
import { getApiUrl } from "../config/api";

// Up to this many users are loaded. The backend's own search only matches the
// start of an email address, so the pickers filter this list themselves.
const DIRECTORY_LIMIT = 500;

// The people and groups the signed-in user can pick from - the one loader
// behind every user picker: Usage & Billing's "Choose a user", the Groups
// tab's "Add members" and the newsletter's audience picker.
//
// Reads GET /api/admin/users (users.read or trend.subscribers.manage) and
// GET /api/admin/groups; the backend leaves out hidden groups and their
// members. Groups are best-effort: without access to them the users still
// load, and callers show a group's id instead of its name.
//
// Loads when the component mounts; reload() fetches again, e.g. after people
// were moved between groups.
export function useUserDirectory() {
  const { currentUser } = useAuth();
  const [version, setVersion] = useState(0);
  const [state, setState] = useState({
    status: "loading", // "loading" | "ok" | "error"
    users: [],
    groups: [],
    hasNext: false,
    error: null,
  });

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    const get = async (path, token) => {
      const response = await fetch(`${getApiUrl()}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // A proxy error page (a 502 from Cloud Run, say) isn't JSON.
      const data = await response.json().catch(() => ({}));
      // A failed Firestore read still answers success, with an error field.
      if (!response.ok || !data.success || data.error) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }
      return data;
    };

    const load = async () => {
      setState((s) => ({ ...s, status: "loading", error: null }));
      try {
        const token = await currentUser.getIdToken();
        const [users, groups] = await Promise.allSettled([
          get(`/api/admin/users?limit=${DIRECTORY_LIMIT}`, token),
          get("/api/admin/groups", token),
        ]);
        if (users.status === "rejected") throw users.reason;
        if (cancelled) return;
        setState({
          status: "ok",
          users: users.value.users || [],
          groups: groups.status === "fulfilled" ? groups.value.groups || [] : [],
          hasNext: Boolean(users.value.hasNext),
          error: null,
        });
      } catch (err) {
        console.error("Failed to load the user directory:", err);
        if (!cancelled) {
          setState((s) => ({ ...s, status: "error", error: err.message }));
        }
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [currentUser, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);

  return { ...state, limit: DIRECTORY_LIMIT, reload };
}
